import { select, call, takeLatest, put } from 'redux-saga/effects';

import { DistrictActions } from '../constants';
import { DistrictApi } from 'src/services/end-points';
import { StoreState } from '../reducers';
import { PaginationService } from '../../services/api-handlers/pagination';

import {
    DeleteDistrictAction,
    GetDistrictAction,
    GetDistrictsAction,
    PersistDistrictAction,
    CreateFilesDistrictAction,
    GetFilesAction,
    DeleteFileDistrictAction,
} from '../actions/district-actions';
import sortingService from '../../services/sorting';
import {
    handleError,
    resolveApiCall,
    ResolverApiFailure,
    ResolverApiSuccess,
} from '../../services/api-handlers/api-resolver';
import { SubmissionError } from 'redux-form';
import { makeFormErrors } from '../../components/redux-form/utils/make-form-errors';
import { convertValuesToFormData } from '../../components/redux-form/utils/values-to-form-data';
import { push } from 'connected-react-router';
import Router from '../../navigation/router';
import routes from '../../navigation/routes';
import { changeToZeroMomentDate } from '../../helpers/timeHelpers';

function* handleFetchDistricts(action: GetDistrictsAction) {
    const oldData = yield select((state: StoreState) => state.districts.data);
    const { filters, sorting, page, itemsPerPage } = action.payload;
    let filterProps: AnyObject = { ...filters };
    if (filters?.createdAt) {
        filterProps = {
            ...filters,
            'createdAt[before]': changeToZeroMomentDate(`${filters.createdAt}`),
            'createdAt[after]': changeToZeroMomentDate(`${filters.createdAt}`),
        };
        delete filterProps.createdAt;
    }
    yield PaginationService.setPageSize(itemsPerPage).resolveApiCall(action, page, oldData, () =>
        DistrictApi.getDistricts({
            ...filterProps,
            order: sortingService.makeOrder(sorting),
            page,
            itemsPerPage,
            pagination: itemsPerPage === -1 ? 0 : 1,
        }),
    );
}

function* handleFetchDistrictsSmall(action: GetDistrictsAction) {
    const oldData = yield select((state: StoreState) => state.districts.data);
    const { filters, sorting, page, itemsPerPage } = action.payload;

    yield PaginationService.setPageSize(itemsPerPage).resolveApiCall(action, page, oldData, () =>
        DistrictApi.getDistrictsSmall({
            ...filters,
            order: sortingService.makeOrder(sorting),
            page,
            itemsPerPage,
            pagination: itemsPerPage === -1 ? 0 : 1,
        }),
    );
}

function* handleFetchDistrict(action: GetDistrictAction) {
    const {
        payload: { id },
    } = action;

    yield resolveApiCall(action, () => DistrictApi.getDistrict(id));
}

function* handleDeleteDistrict(action: DeleteDistrictAction) {
    const {
        payload: { id, onSuccess, onError },
    } = action;

    yield resolveApiCall(
        action,
        () => DistrictApi.deleteDistrict(id),
        function () {
            if (onSuccess) {
                onSuccess();
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                if (onError) {
                    onError(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
                }
            }
            handleError(result);
        },
    );
}

function* handlePersistDistrict(action: PersistDistrictAction) {
    const { payload } = action;
    const {
        data: { id, ...restData },
        files,
        callbacks,
    } = payload;

    yield resolveApiCall(
        action,
        () => (id ? DistrictApi.updateDistrict(id, restData) : DistrictApi.createDistrict(restData)),
        function* (data: ResolverApiSuccess) {
            const actionFiles: CreateFilesDistrictAction = {
                type: DistrictActions.CREATE_FILES_DISTRICT,
                payload: {
                    id: data.payload.id,
                    files,
                    callbacks,
                },
            };
            yield call(handleCreateFilesDistrict, actionFiles);
            yield call(callbacks.resolve);
            if (!id) {
                yield put(push(Router.generate(routes.DISTRICTS_LIST)));
            }
        },
        function* (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                yield call(
                    callbacks.reject,
                    result.error instanceof SubmissionError
                        ? result.error
                        : new SubmissionError({
                              _error: result.error.message ?? 'Incorrect response',
                              ...makeFormErrors(result.error.response?.data),
                          }),
                );
            }
            yield handleError(result);
        },
    );
}

function* handleGetFilesDistrict(action: GetFilesAction) {
    const {
        payload: { id },
    } = action;

    yield resolveApiCall(action, () => DistrictApi.getFilesDistrict(id));
}

function* handleCreateFilesDistrict(action: CreateFilesDistrictAction) {
    const {
        payload: { id, files, callbacks },
    } = action;

    for (let i = 0; i < files.length; i++) {
        if (files[i].id || files[i]?.attach) {
            continue;
        }
        const formData = convertValuesToFormData({ district: id, attachFile: files[i] }, '');
        yield resolveApiCall(
            action,
            () => DistrictApi.createFilesDistrict(formData),
            function* (data: ResolverApiSuccess) {
                yield call(callbacks.resolve);
            },
            function* (result: ResolverApiFailure) {
                if (result.error && result.error.response && result.error.response.status) {
                    yield call(
                        callbacks.reject,
                        result.error instanceof SubmissionError
                            ? result.error
                            : new SubmissionError({
                                  _error: result.error.message ?? 'Incorrect response',
                                  ...makeFormErrors(result.error.response?.data),
                              }),
                    );
                }
                yield handleError(result);
            },
        );
    }
}

function* handleDeleteFileDistrict(action: DeleteFileDistrictAction) {
    const {
        payload: { id, onSuccess, onError },
    } = action;

    yield resolveApiCall(
        action,
        () => DistrictApi.deleteFileDistrict(id),
        function (data) {
            if (onSuccess) {
                onSuccess();
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                if (onError) {
                    onError(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
                }
            }
            handleError(result);
        },
    );
}

export default function* () {
    yield takeLatest(DistrictActions.FETCH_DISTRICTS, handleFetchDistricts);
    yield takeLatest(DistrictActions.FETCH_DISTRICTS_SIMPLY, handleFetchDistrictsSmall);
    yield takeLatest(DistrictActions.FETCH_DISTRICT, handleFetchDistrict);
    yield takeLatest(DistrictActions.DELETE_DISTRICT, handleDeleteDistrict);
    yield takeLatest(DistrictActions.PERSIST_DISTRICT, handlePersistDistrict);
    yield takeLatest(DistrictActions.DELETE_FILE_DISTRICT, handleDeleteFileDistrict);
    yield takeLatest(DistrictActions.CREATE_FILES_DISTRICT, handleCreateFilesDistrict);
    yield takeLatest(DistrictActions.FETCH_FILES_DISTRICT, handleGetFilesDistrict);
}
